import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DashboardsContext from 'contexts/DashboardsContext';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import {
  buildShowEntityApp,
  buildShowEntityAppContentTab,
} from 'navigation/appRoutes';
import BoardsContext from '../../../contexts/BoardsContext';
import ReportsContext from '../../../contexts/ReportsContext';
import isPerformanceBoard from '../../../isPerformanceBoard';
import { PortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import DatasetDefinitionsContext from '../../../contexts/DatasetDefinitionsContext';
import { useParams } from 'react-router-dom';
import NavSectionContext from '../../../navigation/Authenticated/NavSectionContext';
import {
  V3AppsContext,
  V3SelectedAppContext,
} from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';

export const useBuildV3ResourceNavigatorAppLink = () => {
  const { selectedProductApp, selectedProduct } =
    useContext(V3SelectedAppContext);
  const { getProductAppsUserHasAccessTo } = useContext(V3AppsContext);
  const { selectedItem } = useContext(NavSectionContext);

  const prefix = useMemo(() => {
    if (!selectedProductApp || !selectedProduct || !selectedItem) {
      return undefined;
    }

    const availableApps = getProductAppsUserHasAccessTo(selectedProduct);
    if (availableApps.length === 1) {
      return `/apps/${selectedProduct.urlSlug}/${selectedItem.key}`;
    }
    return `/apps/${selectedProduct.urlSlug}/${selectedProductApp.urlSlug}/${selectedItem.key}`;
  }, [
    getProductAppsUserHasAccessTo,
    selectedItem,
    selectedProduct,
    selectedProductApp,
  ]);

  const buildV3ResourceNavigatorAppLink = useCallback(
    ({ entityFieldValue }: { entityFieldValue: string }) => {
      if (!prefix) {
        return;
      }

      return `${prefix}?entity=${entityFieldValue}`;
    },
    [prefix],
  );

  return buildV3ResourceNavigatorAppLink;
};

export const useBuildEntityLink = (
  entity: EntityDetails.Entity | undefined,
) => {
  const { boards } = useContext(BoardsContext);
  const { allReports } = useContext(ReportsContext);
  const { allDashboards } = useContext(DashboardsContext);
  const { entityDetailsApps } = useContext(EntityDefinitionsContext);
  const { selectedPortal } = useContext(PortalsContext);
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const buildV3ResourceNavigatorAppLink = useBuildV3ResourceNavigatorAppLink();

  const navParams = useParams<{
    entityAppSlug?: string;
    tabType?: string;
    tabId?: string;
  }>();

  const entityApp = useMemo((): EntityDetails.App | undefined => {
    if (!navParams.entityAppSlug) {
      return undefined;
    }

    return entityDetailsApps.find(
      (app) => app.urlSlug === navParams.entityAppSlug,
    );
  }, [entityDetailsApps, navParams.entityAppSlug]);

  /**
   * If we are currently in an entity app, we want to keep the selected tab
   * active. This function will return the relevant link for the active
   * tab, if we are currently in an entity app
   */
  const getInAppLink = useCallback(
    (value: string): string | undefined => {
      const { tabId, tabType } = navParams;

      if (entityApp && tabId && tabType) {
        return buildShowEntityAppContentTab({
          entityApp,
          tabType,
          tabId,
          portal: selectedPortal,
          value,
        });
      }

      return undefined;
    },
    [entityApp, navParams, selectedPortal],
  );

  /**
   * If we are in an entity app at the moment, build the link for that app.
   *
   * Otherwise, build the link for the default app of the entity provided
   * to this hook
   */
  const buildLink = useCallback(
    (value: string, justCreatedApp?: EntityDetails.App) => {
      if (!entity) {
        return undefined;
      }

      const v3ResourceNavLink = navParams.tabId
        ? buildV3ResourceNavigatorAppLink({
            entityFieldValue: value,
          })
        : undefined;

      if (v3ResourceNavLink) {
        return v3ResourceNavLink;
      }

      const inAppLink = getInAppLink(value);
      if (inAppLink) {
        return inAppLink;
      }

      const app = (() => {
        if (justCreatedApp) {
          return justCreatedApp;
        }

        const apps = entityDetailsApps.filter((a) => a.entityId === entity.id);
        const defaultApp = apps.find((a) => a.id === entity.defaultAppId);
        return defaultApp ? defaultApp : apps.at(0);
      })();
      if (!app) {
        return undefined;
      }

      const performanceBoards = boards.filter(isPerformanceBoard);
      const firstTab = app.tabs.find((t) => {
        if (t.type === 'dashboard') {
          return !!allDashboards.find((d) => d.id === t.typeId);
        } else if (t.type === 'report') {
          return !!allReports.find((r) => r.id === t.typeId);
        } else if (t.type === 'board') {
          return !!performanceBoards.filter((b) => b.id === t.typeId);
        } else if (t.type === 'driver-bonus-program-report') {
          return bonusPrograms.some((b) => b.id === t.typeId);
        } else if (t.type === 'driver-bonus-combined') {
          return true;
        } else if (t.type === 'driver-details-tab') {
          return true;
        }
        return undefined;
      });
      if (firstTab) {
        return buildShowEntityAppContentTab({
          entityApp: app,
          value,
          tabType: firstTab.type,
          tabId: firstTab.typeId,
          portal: selectedPortal,
        });
      }
      return buildShowEntityApp({
        entityApp: app,
        value,
        portal: selectedPortal,
      });
    },
    [
      allDashboards,
      allReports,
      boards,
      bonusPrograms,
      buildV3ResourceNavigatorAppLink,
      entity,
      entityDetailsApps,
      getInAppLink,
      navParams.tabId,
      selectedPortal,
    ],
  );

  return buildLink;
};

const useEntityLink = ({
  entity,
  value,
}: {
  entity: EntityDetails.Entity;
  value: string;
}) => {
  const buildLink = useBuildEntityLink(entity);

  const [link, setLink] = useState<string | undefined>(() => buildLink(value));
  useEffect(() => {
    setLink(buildLink(value));
  }, [buildLink, value]);

  return link;
};

export default useEntityLink;
